import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Plus, Loader2, CheckCircle2, XCircle, Edit2, Trash2, AlertTriangle, Paperclip, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Tip from "../components/Tip";
import DatePickerIT from "../components/ui/date-picker-it";
import DocumentPickerModal from "../components/DocumentPickerModal";
import { useModalA11y } from "../lib/useModalA11y";
import { useUnsavedChanges } from "../lib/useUnsavedChanges";

const ITEM_TYPES = [
  ["income_expected","Entrata prevista"],["income_received","Entrata ricevuta"],
  ["fixed_monthly","Costo fisso mensile"],["fixed_annual","Costo fisso annuale"],
  ["tax","Tassa"],["contribution","Contributo previdenziale"],
  ["subscription","Abbonamento"],["credit_card_payment","Pagamento carta credito"],
  ["pac","PAC / Piano investimento"],["planned_purchase","Acquisto pianificato"],
  ["one_off","Una tantum"],["renewal","Rinnovo"],
  ["reserve_fund","Fondo di riserva"],["variable_expense","Spesa variabile"],
];
const FREQ = [
  ["one_off","Una tantum"],["weekly","Settimanale"],["monthly","Mensile"],
  ["bimonthly","Bimestrale"],["quarterly","Trimestrale"],["semiannual","Semestrale"],
  ["yearly","Annuale"],["custom","Personalizzata"],
];
const ENTITIES = [["personal","Personale"],["medical","Medico"],["prime_vision","Prime Vision"],
                  ["real_estate","Real Estate"],["writer","Scrittrice"],["other","Altro"]];
const STATUSES = { template: "Template", planned: "Pianificato", due: "In scadenza",
                    paid: "Pagato", overdue: "Scaduto", cancelled: "Annullato" };

const eur = (n) => n == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n));

export default function FinancesItems() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const filterType = params.get("type") || "";
  const filterStatus = params.get("status") || "";
  const filterTypes = filterType.split(",").filter(Boolean);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      // Backend supporta ora comma-separated su `type` e `status`.
      const p = {};
      if (filterType) p.type = filterType;
      if (filterStatus) p.status = filterStatus;
      setItems(await api.financesListItems(p));
    } catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType, filterStatus]);
  useEffect(() => {
    if (params.get("new") === "1") {
      // Optional deep-link prefill from AI Suggest CTA (?name=…&type=…&receipt=<doc_id>)
      const prefName = params.get("name");
      const prefType = params.get("type");
      const prefStatus = params.get("status");
      const prefReceipt = params.get("receipt");
      const prefAmount = params.get("amount");
      const prefill = (prefName || prefType || prefStatus || prefReceipt) ? {
        name: prefName || "",
        item_type: prefType || undefined,
        status: prefStatus || undefined,
        receipt_document_id: prefReceipt || undefined,
        amount_expected: prefAmount || undefined,
      } : null;
      setEditing(null);
      setPrefill(prefill);
      setFormOpen(true);
    }
    /* eslint-disable-next-line */
  }, []);

  const markPaid = async (item) => {
    const amount = prompt("Importo effettivo pagato (€)", item.amount_expected || "");
    if (!amount) return;
    const paid_date = prompt("Data pagamento (AAAA-MM-GG)", new Date().toISOString().slice(0, 10));
    if (!paid_date) return;
    try {
      await api.financesMarkPaid(item.id, { amount_actual: parseFloat(amount), paid_date });
      toast.success("Pagamento registrato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const cancel = async (item) => {
    if (!confirm(`Annullare "${item.name}"?`)) return;
    try { await api.financesMarkCancelled(item.id); toast.success("Annullato"); load(); }
    catch { toast.error("Errore"); }
  };

  const del = async (item) => {
    if (!confirm(`Elimina definitivamente "${item.name}"?`)) return;
    try { await api.financesDeleteItem(item.id); toast.success("Eliminato"); load(); }
    catch { toast.error("Errore"); }
  };

  const openEdit = async (it) => {
    // Load full item with details from backend (details not returned by list)
    try {
      const full = await api.financesGetItem(it.id);
      setEditing(full);
    } catch {
      setEditing(it);
    }
    setPrefill(null);
    setFormOpen(true);
  };
  const openNew = () => { setEditing(null); setPrefill(null); setFormOpen(true); };

  return (
    <div className="space-y-6" data-testid="finances-items-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Finanze</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">Voci finanziarie</h1>
          <p className="mt-2 text-sm opacity-80">Ogni voce è pianificata da te. I pagamenti vanno confermati manualmente.</p>
        </div>
        <Tip label="Aggiungi una nuova voce">
          <button onClick={openNew} className="pxos-btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="finances-items-new-btn">
            <Plus size={14} /> Nuova voce
          </button>
        </Tip>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <select className="pxos-input w-48" value={filterTypes.length === 1 ? filterTypes[0] : ""} onChange={(e) => setParams((p) => { const n = new URLSearchParams(p); if (e.target.value) n.set("type", e.target.value); else n.delete("type"); return n; })} data-testid="filter-type" disabled={filterTypes.length > 1}>
          <option value="">{filterTypes.length > 1 ? `${filterTypes.length} tipi selezionati` : "Tutti i tipi"}</option>
          {ITEM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {filterTypes.length > 1 && (
          <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "var(--pxos-primary-light)", color: "var(--pxos-text-dark)" }} data-testid="filter-type-chip">
            Filtro attivo: {filterTypes.map((t) => ITEM_TYPES.find(([v]) => v === t)?.[1] || t).join(", ")}
            <button onClick={() => setParams((p) => { const n = new URLSearchParams(p); n.delete("type"); return n; })}
                    className="hover:opacity-70" aria-label="Rimuovi filtro" data-testid="filter-type-clear">✕</button>
          </span>
        )}
        <select className="pxos-input w-48" value={filterStatus} onChange={(e) => setParams((p) => { const n = new URLSearchParams(p); if (e.target.value) n.set("status", e.target.value); else n.delete("status"); return n; })} data-testid="filter-status">
          <option value="">Tutti gli stati</option>
          {Object.entries(STATUSES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {loading ? <div className="opacity-70 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Caricamento…</div>
       : err ? <div className="pxos-card p-4" style={{ color: "#B42318" }}>{err}</div>
       : items.length === 0 ? (
        <div className="pxos-card p-10 text-center opacity-70">
          <div className="mb-3">Nessuna voce corrisponde ai filtri.</div>
          <button onClick={openNew} className="pxos-btn-primary inline-flex items-center gap-1.5 text-sm"><Plus size={14}/> Aggiungi la prima voce</button>
        </div>
       ) : (
        <ul className="pxos-card divide-y" data-testid="finances-items-list" style={{ borderColor: "var(--pxos-border)" }}>
          {items.map((it) => (
            <li key={it.id} className="flex items-center gap-3 p-3" data-testid="finances-item-row">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium truncate" style={{ color: "var(--pxos-text-dark)" }}>{it.name}</div>
                  <StatusBadge status={it.status} />
                  {(it.status === "template" || it.amount_expected == null) && (
                    <Tip label="Manca l'importo. Modifica la voce per completarla."><AlertTriangle size={14} style={{ color: "#8A5A00" }} /></Tip>
                  )}
                </div>
                <div className="text-xs opacity-70 mt-0.5" style={{ color: "var(--pxos-text-dark)" }}>
                  {ITEM_TYPES.find(([v]) => v === it.item_type)?.[1] || it.item_type} · {FREQ.find(([v]) => v === it.frequency)?.[1] || it.frequency}
                  {it.due_date && <> · Scad. {new Date(it.due_date).toLocaleDateString("it-IT")}</>}
                  {it.provider_name && <> · {it.provider_name}</>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-semibold" style={{ color: "var(--pxos-text-dark)" }}>{eur(it.amount_actual ?? it.amount_expected)}</div>
                {it.amount_actual != null && it.amount_expected != null && it.amount_actual !== it.amount_expected && (
                  <div className="text-[10px] opacity-60">Previsto: {eur(it.amount_expected)}</div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {it.status !== "paid" && it.status !== "cancelled" && it.status !== "template" && (
                  <Tip label="Segna come pagato"><button onClick={() => markPaid(it)} className="p-2 rounded-full hover:bg-black/5" data-testid="btn-mark-paid" aria-label="Segna pagato"><CheckCircle2 size={14} style={{ color: "#1F7A2C" }} /></button></Tip>
                )}
                <Tip label="Modifica voce"><button onClick={() => openEdit(it)} className="p-2 rounded-full hover:bg-black/5" data-testid="btn-edit-item" aria-label="Modifica"><Edit2 size={14} /></button></Tip>
                {it.status !== "cancelled" && (
                  <Tip label="Annulla voce"><button onClick={() => cancel(it)} className="p-2 rounded-full hover:bg-black/5" data-testid="btn-cancel-item" aria-label="Annulla"><XCircle size={14} /></button></Tip>
                )}
                <Tip label="Elimina voce"><button onClick={() => del(it)} className="p-2 rounded-full hover:bg-red-50" data-testid="btn-delete-item" aria-label="Elimina"><Trash2 size={14} className="text-red-500" /></button></Tip>
              </div>
            </li>
          ))}
        </ul>
       )}

      <ItemForm open={formOpen} onClose={() => setFormOpen(false)} initial={editing} prefill={prefill} onSaved={() => { load(); setFormOpen(false); }} />
    </div>
  );
}

function StatusBadge({ status }) {
  const color = {
    template: "#8A5A00", planned: "#4A6078", due: "#B42318",
    paid: "#1F7A2C", overdue: "#B42318", cancelled: "#6B7280",
  }[status] || "#4A6078";
  const bg = {
    template: "#FFF8E1", planned: "#E7ECF3", due: "#FFF0F0",
    paid: "#E7F3E9", overdue: "#FFF0F0", cancelled: "#F3F4F6",
  }[status] || "#E7ECF3";
  return <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-semibold" style={{ background: bg, color }}>{STATUSES[status] || status}</span>;
}

function ItemForm({ open, onClose, onSaved, initial, prefill = null }) {
  const empty = { name: "", description: "", item_type: "fixed_monthly", entity_type: "personal",
                  amount_expected: "", amount_actual: "", due_date: "", frequency: "monthly",
                  provider_name: "", notes: "", status: "planned",
                  receipt_document_id: null,
                  details: {} };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [attachedDoc, setAttachedDoc] = useState(null); // {id, title/filename, category}
  const [pickerOpen, setPickerOpen] = useState(false);
  const dialogRef = useRef(null);

  useModalA11y(dialogRef, open, () => tryClose());
  useUnsavedChanges(dirty && open);

  useEffect(() => {
    if (!open) return;
    setDirty(false);
    if (initial) {
      setForm({
        name: initial.name || "", description: initial.description || "",
        item_type: initial.item_type || "fixed_monthly",
        entity_type: initial.entity_type || "personal",
        amount_expected: initial.amount_expected || "",
        amount_actual: initial.amount_actual || "",
        due_date: initial.due_date || "",
        frequency: initial.frequency || "monthly",
        provider_name: initial.provider_name || "",
        notes: initial.notes || "",
        status: initial.status || "planned",
        receipt_document_id: initial.receipt_document_id || null,
        details: initial.details || {},
      });
      // Fetch attached doc metadata for display badge
      if (initial.receipt_document_id) {
        api.docsGet(initial.receipt_document_id).then(setAttachedDoc).catch(() => setAttachedDoc(null));
      } else {
        setAttachedDoc(null);
      }
    } else if (prefill) {
      setForm({
        ...empty,
        name: prefill.name || "",
        item_type: prefill.item_type || empty.item_type,
        status: prefill.status || empty.status,
        amount_expected: prefill.amount_expected || "",
        receipt_document_id: prefill.receipt_document_id || null,
      });
      if (prefill.receipt_document_id) {
        api.docsGet(prefill.receipt_document_id).then(setAttachedDoc).catch(() => setAttachedDoc(null));
      } else {
        setAttachedDoc(null);
      }
    } else {
      setForm(empty);
      setAttachedDoc(null);
    }
  // eslint-disable-next-line
  }, [open, initial, prefill]);

  const setF = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const setDetail = (k, v) => { setForm((f) => ({ ...f, details: { ...(f.details || {}), [k]: v } })); setDirty(true); };

  const tryClose = () => {
    if (dirty && !window.confirm("Modifiche non salvate. Vuoi davvero uscire?")) return;
    setDirty(false); onClose?.();
  };

  if (!open) return null;

  const needsDetails = form.item_type === "credit_card_payment" ||
                       form.item_type === "subscription" ||
                       form.item_type === "pac";

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    let details = null;
    if (form.item_type === "credit_card_payment") {
      const cd = form.details || {};
      if (!cd.closing_day || !cd.charge_day) {
        setBusy(false);
        toast.error("Servono giorno chiusura estratto conto e giorno addebito.");
        return;
      }
      details = {
        closing_day: parseInt(cd.closing_day, 10),
        charge_day: parseInt(cd.charge_day, 10),
        estimated_amount: cd.estimated_amount === "" || cd.estimated_amount == null ? null : parseFloat(cd.estimated_amount),
        actual_amount: cd.actual_amount === "" || cd.actual_amount == null ? null : parseFloat(cd.actual_amount),
      };
    } else if (form.item_type === "subscription") {
      const sd = form.details || {};
      details = {
        last_used_at: sd.last_used_at || null,
        perceived_utility: sd.perceived_utility || null,
        cancellation_url: sd.cancellation_url || null,
      };
    } else if (form.item_type === "pac") {
      const pd = form.details || {};
      if (!pd.execution_day) {
        setBusy(false);
        toast.error("Serve giorno di esecuzione (1-28) per il PAC.");
        return;
      }
      details = {
        execution_day: parseInt(pd.execution_day, 10),
        cumulative_invested: pd.cumulative_invested === "" || pd.cumulative_invested == null ? null : parseFloat(pd.cumulative_invested),
      };
    }
    const payload = {
      ...form,
      amount_expected: form.amount_expected === "" ? null : parseFloat(form.amount_expected),
      amount_actual: form.amount_actual === "" ? null : parseFloat(form.amount_actual),
      due_date: form.due_date || null,
      receipt_document_id: form.receipt_document_id || null,
      details,
    };
    if (payload.details === null) delete payload.details;

    try {
      if (initial) await api.financesUpdateItem(initial.id, payload);
      else await api.financesCreateItem(payload);
      toast.success(initial ? "Voce aggiornata" : "Voce creata");
      setDirty(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={tryClose} data-testid="finance-item-form">
      <form onSubmit={submit}
            ref={dialogRef}
            role="dialog" aria-modal="true" aria-labelledby="finance-form-title"
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--pxos-border)" }}>
          <h3 id="finance-form-title" className="font-display text-xl font-semibold">{initial ? "Modifica voce" : "Nuova voce finanziaria"}</h3>
          <button type="button" onClick={tryClose} aria-label="Chiudi" data-testid="finance-form-close">✕</button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Nome</span>
            <input required value={form.name} onChange={(e) => setF({ name: e.target.value })} className="pxos-input" data-testid="finance-name" /></label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Tipo</span>
              <select value={form.item_type} onChange={(e) => setF({ item_type: e.target.value })} className="pxos-input" data-testid="finance-type">
                {ITEM_TYPES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Frequenza</span>
              <select value={form.frequency} onChange={(e) => setF({ frequency: e.target.value })} className="pxos-input" data-testid="finance-frequency">
                {FREQ.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Entità</span>
              <select value={form.entity_type} onChange={(e) => setF({ entity_type: e.target.value })} className="pxos-input" data-testid="finance-entity">
                {ENTITIES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Stato</span>
              <select value={form.status} onChange={(e) => setF({ status: e.target.value })} className="pxos-input" data-testid="finance-status">
                {Object.entries(STATUSES).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Importo previsto €</span>
              <input type="number" step="0.01" value={form.amount_expected} onChange={(e) => setF({ amount_expected: e.target.value })} className="pxos-input" data-testid="finance-amount-expected" /></label>
            <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Scadenza</span>
              <DatePickerIT value={form.due_date} onChange={(v) => setF({ due_date: v })} testId="finance-due-date" placeholder="gg/mm/aaaa" />
            </label>
          </div>
          <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Provider / Beneficiario</span>
            <input value={form.provider_name} onChange={(e) => setF({ provider_name: e.target.value })} className="pxos-input" data-testid="finance-provider" /></label>
          <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Note</span>
            <textarea rows="2" value={form.notes} onChange={(e) => setF({ notes: e.target.value })} className="pxos-input" data-testid="finance-notes" /></label>

          {/* Enhancement A: allega documento (ricevuta/fattura) */}
          <div className="pxos-card p-3 space-y-2" style={{ background: "#FAFBFF", borderColor: "var(--pxos-border)" }} data-testid="finance-attachment-block">
            <div className="text-xs font-mono uppercase tracking-widest opacity-70 flex items-center gap-2">
              <Paperclip className="h-3 w-3" /> Documento allegato
            </div>
            {form.receipt_document_id && attachedDoc ? (
              <div className="flex items-center gap-2 text-sm" data-testid="finance-attached-doc">
                <FileText className="h-4 w-4 opacity-70 flex-shrink-0" />
                <Link
                  to={`/documenti/${attachedDoc.id}`}
                  className="flex-1 min-w-0 truncate hover:underline text-[var(--pxos-accent-dark)]"
                  data-testid="finance-attached-doc-link"
                >
                  {attachedDoc.title || attachedDoc.filename || "Documento"}
                </Link>
                {attachedDoc.category && (
                  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-semibold"
                        style={{ background: "#E7ECF3", color: "#4A6078" }}>
                    {attachedDoc.category}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => { setF({ receipt_document_id: null }); setAttachedDoc(null); }}
                  className="p-1 rounded-full hover:bg-red-50 text-red-600"
                  aria-label="Scollega documento"
                  data-testid="finance-detach-doc"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : form.receipt_document_id && !attachedDoc ? (
              <div className="text-xs opacity-60">Caricamento documento…</div>
            ) : (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-2 rounded-full border border-dashed px-3 py-1.5 text-xs font-semibold hover:bg-[var(--pxos-surface-soft)]"
                style={{ borderColor: "var(--pxos-border)" }}
                data-testid="finance-attach-doc-btn"
              >
                <Paperclip className="h-3.5 w-3.5" /> Allega documento
              </button>
            )}
          </div>

          {/* Contextual details per type */}
          {needsDetails && (
            <div className="pxos-card p-3 space-y-3" style={{ background: "#F8FAFF", borderColor: "#C3DAF4" }} data-testid={`finance-details-${form.item_type}`}>
              <div className="text-xs font-mono uppercase tracking-widest opacity-70">
                Dati specifici · {form.item_type === "credit_card_payment" ? "Carta di credito" : form.item_type === "subscription" ? "Abbonamento" : "PAC"}
              </div>
              {form.item_type === "credit_card_payment" && (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Giorno chiusura estratto (1-28)</span>
                      <input required type="number" min="1" max="28" value={form.details?.closing_day ?? ""} onChange={(e) => setDetail("closing_day", e.target.value)} className="pxos-input" data-testid="cc-closing-day" /></label>
                    <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Giorno addebito conto (1-28)</span>
                      <input required type="number" min="1" max="28" value={form.details?.charge_day ?? ""} onChange={(e) => setDetail("charge_day", e.target.value)} className="pxos-input" data-testid="cc-charge-day" /></label>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Stima importo € (opz.)</span>
                      <input type="number" step="0.01" value={form.details?.estimated_amount ?? ""} onChange={(e) => setDetail("estimated_amount", e.target.value)} className="pxos-input" data-testid="cc-estimated" /></label>
                    <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Importo effettivo € (opz.)</span>
                      <input type="number" step="0.01" value={form.details?.actual_amount ?? ""} onChange={(e) => setDetail("actual_amount", e.target.value)} className="pxos-input" data-testid="cc-actual" /></label>
                  </div>
                </>
              )}
              {form.item_type === "subscription" && (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Ultimo utilizzo (opz.)</span>
                      <DatePickerIT value={form.details?.last_used_at ?? ""} onChange={(v) => setDetail("last_used_at", v)} testId="sub-last-used" placeholder="gg/mm/aaaa" />
                    </label>
                    <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Utilità percepita</span>
                      <select value={form.details?.perceived_utility ?? ""} onChange={(e) => setDetail("perceived_utility", e.target.value)} className="pxos-input" data-testid="sub-utility">
                        <option value="">—</option>
                        <option value="high">Alta</option>
                        <option value="medium">Media</option>
                        <option value="low">Bassa</option>
                        <option value="unused">Non usato</option>
                      </select></label>
                  </div>
                  <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">URL disdetta (opz.)</span>
                    <input type="url" value={form.details?.cancellation_url ?? ""} onChange={(e) => setDetail("cancellation_url", e.target.value)} className="pxos-input" placeholder="https://…" data-testid="sub-cancel-url" /></label>
                </>
              )}
              {form.item_type === "pac" && (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Giorno esecuzione (1-28)</span>
                      <input required type="number" min="1" max="28" value={form.details?.execution_day ?? ""} onChange={(e) => setDetail("execution_day", e.target.value)} className="pxos-input" data-testid="pac-execution-day" /></label>
                    <label className="block"><span className="text-xs uppercase opacity-70 tracking-widest">Cumulato investito € (opz.)</span>
                      <input type="number" step="0.01" value={form.details?.cumulative_invested ?? ""} onChange={(e) => setDetail("cumulative_invested", e.target.value)} className="pxos-input" data-testid="pac-cumulative" /></label>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--pxos-border)" }}>
          <button type="button" onClick={tryClose} className="rounded-full border px-4 py-2 text-xs font-semibold" data-testid="finance-cancel">Annulla</button>
          <button type="submit" disabled={busy} className="pxos-btn-primary text-xs disabled:opacity-50" data-testid="finance-save">
            {busy ? "Salvataggio…" : (initial ? "Salva modifiche" : "Crea voce")}
          </button>
        </div>
      </form>
      <DocumentPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentId={initial?.id || null}
        onSelect={(doc) => {
          setF({ receipt_document_id: doc.id });
          setAttachedDoc(doc);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}
